import { Heading, Text } from '@radix-ui/themes';
import { BadgeBoolean } from 'components/common/badges/boolean';
import { CommonFormDialog } from 'components/common/dialog/form-dialog';
import { FormSelect } from 'components/form/select';
import { FormTextField } from 'components/form/textfield';
import { ActionSelect } from 'components/layout/action-select';
import { EB } from 'components/layout/error-boundary';
import { InvestorForm } from 'components/main/routes/investors/form';
import { UserForm } from 'components/main/routes/users/form';
import { CommonTable } from 'components/table';
import { AuthContext } from 'contexts/auth';
import { DialogType } from 'enums/dialog';
import { RX } from 'enums/radix';
import { DialogState } from 'interfaces/dialog/state';
import { InvestorType } from 'lib/enums/investor';
import {
  InvestorPrivateWhole,
  ManageInvestorPayload,
} from 'lib/interfaces/database/investor-private';
import { UserDoc } from 'lib/interfaces/database/user';
import { SortUtil } from 'lib/utilities/sort';
import { StringUtil } from 'lib/utilities/string';
import { useContext, useEffect, useMemo, useState } from 'react';
import useInvestorsStore from 'stores/investors';

enum ExtraAction {
  CreateUser = 'CreateUser',
}

export const Investors = () => {
  const { data, filters, create, createUser, load, update, setFilters } =
    useInvestorsStore();

  const { perms } = useContext(AuthContext);
  const { investors: crud } = perms;

  useEffect(() => {
    if (crud?.read) {
      load();
    }
  }, [crud?.read, load]);

  const [search, setSearch] = useState('');

  const filteredData = useMemo(() => {
    const lowerSearch = search.toLowerCase();

    return data
      .filter((m) => !filters.type || m.type === filters.type)
      .filter((m) => {
        if (!search.trim()) {
          return true;
        }

        if (m.preferredName.toLowerCase().includes(lowerSearch)) {
          return true;
        }

        if (m._investor?.code?.toLowerCase().includes(lowerSearch)) {
          return true;
        }

        if (m.fullAddress.toLowerCase().includes(lowerSearch)) {
          return true;
        }

        return false;
      });
  }, [data, filters, search]);

  const [dialog, setDialog] = useState<DialogState>();

  // for creating a user record to grant an investor portal access
  const [dialogUser, setDialogUser] = useState<DialogState>();

  return (
    <EB>
      <Heading>Investors</Heading>
      <CommonTable
        defaultSort={{
          iCol: 1,
          asc: false,
        }}
        toolbarControls={[
          <FormSelect
            placeholder="Select Type"
            defaultValue={filters.type}
            options={Object.values(InvestorType).map((o) => ({
              label: o,
              value: o,
            }))}
            onOptionalChange={(v) =>
              setFilters({
                type: v as InvestorType,
              })
            }
            className="width-250px"
            hideLabel
          />,
          <FormTextField
            placeholder="Search"
            defaultValue={search}
            onChange={setSearch}
            className="width-250px"
            hideLabel
          />,
        ]}
        toolbarActions={[
          {
            hidden: !crud?.create,
            label: 'Create',
            onClick: () => {
              const emptyPayload: ManageInvestorPayload = {
                investor: {},
                investorPrivate: {},
              };

              setDialog({
                key: Date.now(),
                type: DialogType.Create,
                data: emptyPayload,
              });
            },
          },
        ]}
        columns={[
          {
            header: 'Actions',
            rowFn: (item: InvestorPrivateWhole) => (
              <ActionSelect
                options={[
                  {
                    hidden: !crud?.update,
                    label: 'Update',
                    value: DialogType.Update,
                  },
                  {
                    // only for investors w/o a user record; only w/ permission to create users
                    hidden: !!item.userID || !perms.users?.create,
                    label: 'Create User',
                    value: ExtraAction.CreateUser,
                  },
                ]}
                onValueChange={(v) => {
                  // new value never gets set but it does trigger the effect below
                  switch (v) {
                    case DialogType.Update: {
                      if (!item._investor) {
                        console.error(
                          'No investor for investor private record'
                        );
                        return;
                      }

                      const payload: ManageInvestorPayload = {
                        investor: item._investor,
                        investorPrivate: item,
                      };

                      setDialog({
                        key: Date.now(),
                        type: v,
                        data: payload,
                      });
                      break;
                    }

                    case ExtraAction.CreateUser: {
                      const payload: Partial<UserDoc> = {
                        investorID: item.investorID,
                        isAdmin: false,
                        firstName: item.firstName,
                        lastName: item.lastName,
                      };

                      setDialogUser({
                        key: Date.now(),
                        type: DialogType.Create,
                        data: payload,
                      });
                      break;
                    }

                    default: {
                      break;
                    }
                  }
                }}
              />
            ),
          },
          {
            header: 'Created',
            rowFn: (item: InvestorPrivateWhole) =>
              StringUtil.printDateTime(item.createdAt),
            sortFn: (a: InvestorPrivateWhole, b: InvestorPrivateWhole) =>
              SortUtil.dates(a.createdAt, b.createdAt),
          },
          {
            header: 'Code',
            rowFn: (item: InvestorPrivateWhole) => item._investor?.code,
            sortFn: (a: InvestorPrivateWhole, b: InvestorPrivateWhole) =>
              SortUtil.strings(a._investor?.code, b._investor?.code),
          },
          {
            header: 'Preferred Name',
            rowFn: (item: InvestorPrivateWhole) => item.preferredName,
            sortFn: (a: InvestorPrivateWhole, b: InvestorPrivateWhole) =>
              SortUtil.strings(a.preferredName, b.preferredName),
          },
          {
            header: 'Location',
            rowFn: (item: InvestorPrivateWhole) =>
              [item.city, item.province, item.country]
                .filter((m) => m)
                .join(', '),
            sortFn: (a: InvestorPrivateWhole, b: InvestorPrivateWhole) =>
              SortUtil.strings(
                a.country + a.province + a.city,
                b.country + b.province + b.city
              ),
          },
          {
            header: 'Portal Access',
            rowFn: (item: InvestorPrivateWhole) => (
              <BadgeBoolean value={!!item.userID} />
            ),
            sortFn: (a: InvestorPrivateWhole, b: InvestorPrivateWhole) =>
              SortUtil.booleans(!!a.userID, !!b.userID),
          },
        ]}
        data={filteredData}
      />

      {dialog?.type === DialogType.Create && (
        <CommonFormDialog
          key={dialog.key}
          title="Create"
          children={<InvestorForm />}
          formData={dialog.data}
          submitAction={{
            label: 'Save',
            onClick: (item) => create(item),
          }}
          onClose={() => setDialog(undefined)}
        />
      )}

      {dialog?.type === DialogType.Update && (
        <CommonFormDialog
          key={dialog.key}
          title="Update"
          children={<InvestorForm />}
          formData={dialog.data}
          submitAction={{
            label: 'Save',
            onClick: async (item) => {
              const { id: investorPrivateID } = (
                dialog.data as ManageInvestorPayload
              ).investorPrivate;

              if (!investorPrivateID) {
                console.error('No investor private ID');
                return false;
              }

              return await update(investorPrivateID, item);
            },
          }}
          onClose={() => setDialog(undefined)}
        />
      )}

      {dialogUser && (
        <CommonFormDialog
          key={dialogUser.key}
          title="Create User"
          description={
            <Text color={RX.CLR.NEUTRAL}>
              Creating a user for this investor will allow them to access the
              portal via the login credentials provided below.
            </Text>
          }
          children={<UserForm />}
          formData={dialogUser.data}
          submitAction={{
            label: 'Save',
            onClick: async (item: Partial<UserDoc>) => {
              if (!item.investorID) {
                // shouldn't trigger
                return false;
              }

              return createUser(item.investorID, item);
            },
          }}
          onClose={() => setDialogUser(undefined)}
        />
      )}
    </EB>
  );
};
